import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import messages from "../../../../assets/locale/messages";
import useDebounce from "../../../../hooks/useDebounce";
import { companyPermissionModules } from "../../../../utils/Constants";
import {
  extractAdminPortalList,
  extractAdminPortalMessage,
  useAdminModulePermission,
} from "../../../../utils/adminPortalHelpers";
import { showErrorMsg, showSuccessMsg } from "../../../../utils/Helpers";
import {
  deleteAdminCompanyUser,
  getAdminCompanyUsers,
} from "../../../../network/apis/adminCompanyUsers";
import {
  buildCompanyUsersListParams,
  getCompanyUsersListMeta,
  normalizeCompanyUsers,
  resolveCompanyUserId,
} from "../../../Company-portal/TeamsPermissions/employeeMappers";
import { UI_TABLE_DEFAULT_PAGE_SIZE } from "../../../../utils/tablePageSize";
import {
  adminCompanyTeamRoutes,
  resolveTeamSubtabFromSearch,
} from "../adminCompanyTeamRoutes";

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const useAdminCompanyEmployeesList = (companyId) => {
  const history = useHistory();
  const location = useLocation();
  const lang = useSelector((state) => state.locale.lang);
  const copy = messages[lang].teamManagement;
  const employeesCopy = copy.employees;
  const { shared } = messages[lang];
  const { canAdd, canEdit, canDelete } = useAdminModulePermission(
    companyPermissionModules.employees,
  );

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(UI_TABLE_DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ currentPage: 1, count: 0, pages: 1 });
  const [isLoading, setIsLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);

  const listParams = useMemo(
    () =>
      buildCompanyUsersListParams({
        search: debouncedSearch,
        page,
        pageSize,
      }),
    [debouncedSearch, page, pageSize],
  );

  const fetchEmployees = useCallback(async () => {
    if (!companyId) return;

    setIsLoading(true);
    try {
      const response = await getAdminCompanyUsers({
        ...listParams,
        company_id: companyId,
      });
      const list = extractAdminPortalList(response);
      setRows(normalizeCompanyUsers(list));
      setMeta(getCompanyUsersListMeta(response));
    } catch (error) {
      setRows([]);
      setMeta({ currentPage: 1, count: 0, pages: 1 });
      showErrorMsg(error?.response?.data?.message || shared.generalErrMsg);
    } finally {
      setIsLoading(false);
    }
  }, [companyId, listParams, shared.generalErrMsg]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees, lang]);

  const total = meta.count || 0;
  const pages = meta.pages || Math.max(Math.ceil(total / pageSize), 1);
  const rangeFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, total);

  const handleSearchChange = useCallback((event) => {
    setSearch(event.target.value);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage);
  }, []);

  const handlePageSizeChange = useCallback((nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  }, []);

  const handleAddEmployee = useCallback(() => {
    if (!companyId) return;
    history.push(adminCompanyTeamRoutes.employeeCreate(companyId));
  }, [history, companyId]);

  const handleViewEmployee = useCallback(
    (employee) => {
      const employeeId = resolveCompanyUserId(employee);
      if (!employeeId || !companyId) return;
      const returnSubtab = resolveTeamSubtabFromSearch(location.search);
      history.push({
        pathname: adminCompanyTeamRoutes.employeeView(companyId, employeeId),
        state: { employeeData: employee, companyId, returnSubtab },
      });
    },
    [history, companyId, location.search],
  );

  const handleEditEmployee = useCallback(
    (employee) => {
      const employeeId = resolveCompanyUserId(employee);
      if (!employeeId || !companyId) return;
      const returnSubtab = resolveTeamSubtabFromSearch(location.search);
      history.push({
        pathname: adminCompanyTeamRoutes.employeeView(companyId, employeeId),
        state: {
          startInEdit: true,
          employeeData: employee,
          companyId,
          returnSubtab,
        },
      });
    },
    [history, companyId, location.search],
  );

  const handleRemove = useCallback((employee) => {
    setEmployeeToDelete(employee);
  }, []);

  const closeDeleteModal = useCallback(() => {
    if (deleting) return;
    setEmployeeToDelete(null);
  }, [deleting]);

  const confirmDeleteEmployee = useCallback(async () => {
    if (!employeeToDelete || deleting) return;

    setDeleting(true);
    try {
      const response = await deleteAdminCompanyUser(employeeToDelete.id);
      const message = extractAdminPortalMessage(response);
      if (message) showSuccessMsg(message, true);
      else showSuccessMsg("deletedSuccessfully");
      setEmployeeToDelete(null);
      fetchEmployees();
    } catch (error) {
      showErrorMsg(error?.response?.data?.message || shared.generalErrMsg);
    } finally {
      setDeleting(false);
    }
  }, [employeeToDelete, deleting, fetchEmployees, shared.generalErrMsg]);

  return {
    copy,
    employeesCopy,
    search,
    debouncedSearch,
    rows,
    page,
    pageSize,
    pages,
    rangeFrom,
    rangeTo,
    total,
    isLoading,
    deleting,
    employeeToDelete,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    canAdd,
    canEdit,
    canDelete,
    handleSearchChange,
    handlePageChange,
    handlePageSizeChange,
    handleAddEmployee,
    handleViewEmployee,
    handleEditEmployee,
    handleRemove,
    closeDeleteModal,
    confirmDeleteEmployee,
  };
};

export default useAdminCompanyEmployeesList;
